import { action, computed, observable } from 'mobx'
import { Observable, switchMap, tap } from 'rxjs'
import { toStream } from '@/utils/mobx-util'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { localStorage } from '@/infra/storage'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import type { FetchJobFn } from '@/features/jobs'
import { Id } from '@/infra/id'
import { pluralize } from '@/utils/pluralization-util'
import { links } from '@/utils/link'
import type { NavigateFn } from '@/RootStore'
import { type NotificationsSidebarViewModel } from '@/features/notifications/NotificationsSidebarViewModel'

/**
 * Notifications page view model: notifications threaded by job, beside the
 * job they're about.
 */
export class NotificationsPageViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _requestedJobIds = new Set<string>()

  /**
   * Whether the notification list shows beside the job.
   */
  @observable accessor listVisible = readListVisible()

  /**
   * The job the route shows beside the list, if any.
   */
  @observable accessor selectedJobId: string | null = null

  constructor(
    private readonly _notifications: NotificationsSidebarViewModel,
    private readonly _fetchJob: FetchJobFn,
    private readonly _navigate: NavigateFn,
  ) {
    this._lifecycle.manage(
      // The list switches with the selected workspace; only the shown one is
      // active.
      toStream(() => this.list, true).pipe(
        switchMap(
          (list) =>
            new Observable<never>(() => {
              list.activate()
              return () => list.deactivate()
            }),
        ),
      ),
    )
    this._lifecycle.manage(
      toStream(() => this.list.jobIds, true).pipe(
        tap((jobIds) => this._loadJobs(jobIds)),
      ),
    )
  }

  /**
   * The notification list for the selected workspace.
   */
  @computed
  get list() {
    return this._notifications.list
  }

  /**
   * Available workspaces.
   */
  @computed
  get workspaces() {
    return this._notifications.workspaces
  }

  /**
   * The workspace being filtered on.
   */
  @computed
  get selectedWorkspace() {
    return this._notifications.selectedWorkspace
  }

  /**
   * Whether marking all as read is unavailable.
   */
  @computed
  get isMarkAllAsReadDisabled() {
    return this.list.markAllAsRead.pending || this.list.unreadCount === 0
  }

  /**
   * The job before the selected one in the list, if any.
   */
  @computed
  get previousJobId() {
    return this._adjacentJobId(-1)
  }

  /**
   * The job after the selected one in the list, if any.
   */
  @computed
  get nextJobId() {
    return this._adjacentJobId(1)
  }

  /**
   * Describes how many notifications are unread.
   */
  @computed
  get unreadCountText() {
    const count = this.list.unreadCount
    return count === 0
      ? 'No unread notifications'
      : `${count} unread ${pluralize('notification', count)}`
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Selects a workspace.
   */
  @action.bound
  selectWorkspace(workspace: Workspace) {
    this._notifications.selectWorkspace(workspace)
  }

  /**
   * Selects the job the route shows.
   */
  @action.bound
  selectJob(jobId: string | null) {
    this.selectedJobId = jobId
  }

  /**
   * Toggles the notification list's visibility.
   */
  @action.bound
  toggleListVisibility() {
    this.listVisible = !this.listVisible
    localStorage.setItem(listVisibleKey, String(this.listVisible))
  }

  /**
   * Whether the notification list shows. Too narrow for both panes, the list
   * and the job take turns.
   */
  isListVisible(wide: boolean) {
    return wide ? this.listVisible : !this.selectedJobId
  }

  /**
   * Whether the job pane shows.
   */
  isDetailVisible(wide: boolean) {
    return wide || Boolean(this.selectedJobId)
  }

  /**
   * Opens the job before the selected one.
   */
  @action.bound
  goToPreviousJob() {
    if (this.previousJobId) {
      this._navigate(links.notifications(this.previousJobId))
    }
  }

  /**
   * Opens the job after the selected one.
   */
  @action.bound
  goToNextJob() {
    if (this.nextJobId) {
      this._navigate(links.notifications(this.nextJobId))
    }
  }

  private _adjacentJobId(offset: -1 | 1): string | null {
    const jobIds = this.list.jobIds
    const index = this.selectedJobId ? jobIds.indexOf(this.selectedJobId) : -1
    if (index === -1) {
      return null
    }
    return jobIds[index + offset] ?? null
  }

  /**
   * Loads the jobs that name the list's threads.
   */
  private _loadJobs(jobIds: string[]) {
    jobIds
      .filter((id) => !this._requestedJobIds.has(id))
      .forEach((id) => {
        this._requestedJobIds.add(id)
        // A job the user lost access to keeps its thread, untitled; an aborted
        // or failed fetch is retried on the next visit.
        this._fetchJob(Id.assert(id), undefined, this._lifecycle.signal).catch(
          () => this._requestedJobIds.delete(id),
        )
      })
  }
}

const listVisibleKey = 'Notifications list visible'

function readListVisible() {
  return localStorage.getItem(listVisibleKey) !== 'false'
}
